import React from 'react';
import autobind from 'autobind';
import { observable, makeObservable } from 'mobx';
import { observer, inject } from 'mobx-react';

import { FileViewModel } from 'chaika-frontend-types/frontend-cms';

import { Ehr } from '@patient/store/ehr/Ehr';
import { AppState } from '@store/App/App';
import { EhrApi } from '@api/EhrApi';

import { EhrFiles, EhrFilesProps } from './EhrFiles';
import { AddFile } from './AddFile';

interface Props extends EhrFilesProps {}

interface StoreProps {
    ehr: Ehr;
    app: AppState;
}

const injectableStores: (keyof StoreProps)[] = [Ehr.Name, AppState.Name];

@observer
export class Component extends React.Component<Props & StoreProps> {
    @observable
    private addMode = false;

    constructor(props: Props & StoreProps) {
        super(props);
        makeObservable(this);
    }

    public render(): React.ReactNode {
        const { ehr } = this.props;
        return React.createElement(EhrFiles, {
            addMode: this.addMode,
            files: ehr.files,
            onDeleteFileClick: this.onDeleteFileClick,
            onAddClick: this.handleAddFileClick,
        });
    }

    @autobind
    protected handleAddFileClick(): void {
        this.addMode = true;
        this.props.app.setRightMenu({
            isShowing: true,
            children: React.createElement(AddFile, {
                createFileAssetApi: this.createFileAssetApi,
                setFileUploadedStatusApi: this.setFileUploadedStatusApi,
                onFileAdd: this.onFileAdd,
            }),
            onClickOutside: this.handleCancelAdd,
        });
    }

    @autobind
    protected handleCancelAdd(): void {
        this.props.app.resetRightMenu();
        this.addMode = false;
    }

    @autobind
    protected async createFileAssetApi(fileName: string): Promise<FileViewModel> {
        return EhrApi.addFile(this.props.ehr.id, fileName);
    }

    @autobind
    protected async setFileUploadedStatusApi(fileName: string): Promise<void> {
        return EhrApi.setFileUploadedStatus(this.props.ehr.id, fileName);
    }

    @autobind
    protected onFileAdd(fileAsset: FileViewModel): void {
        this.props.ehr.addFile(fileAsset);
        this.forceUpdate();
    }

    @autobind
    protected async onDeleteFileClick(fileName: string): Promise<void> {
        const { ehr } = this.props;

        await EhrApi.deleteFile(ehr.id, fileName);

        ehr.deleteFile(fileName);
        this.forceUpdate();
    }
}

export const EhrFilesContainer = inject<Props, StoreProps>(...injectableStores)(Component);
